import { RenderStateCamera } from "@novorender/api";

import { log } from "utils/log";

import type { DrawingAB, ModelAB, SheetProjection } from "./types";

const l = log.miniMap.utils;

// Kept free of any dependency beyond the logger and the local types: the slice
// builds projections (see `selectDrawingPlacements`), and importing
// `miniMapUtils` from there would drag `fetchDrawingData` — and with it pdfjs —
// into the store's module graph.

/**
 * Resolve a drawing's two-point calibration into the scalars needed to place a
 * model-space position on its sheet.
 *
 * Built once per drawing rather than per use because sheet selection projects
 * the camera onto *every* calibrated sheet on *every* camera update. Doing the
 * trigonometry and gl-matrix allocations there put a few thousand short-lived
 * Float32Arrays per second through the render loop.
 */
export function createSheetProjection(modelAB: ModelAB, drawingAB: DrawingAB): SheetProjection {
    const { pointA: modelA, pointB: modelB } = modelAB;
    const { pointA: drawingA, pointB: drawingB, sheetDimensions } = drawingAB;

    const modelVecX = modelB[0] - modelA[0];
    const modelVecY = modelB[1] - modelA[1];
    const drawingVecX = drawingB[0] - drawingA[0];
    const drawingVecY = drawingB[1] - drawingA[1];

    const modelDist = Math.hypot(modelVecX, modelVecY);
    const drawingDist = Math.hypot(drawingVecX, drawingVecY);

    const sheetWidth = sheetDimensions[0];
    const sheetHeight = sheetDimensions[1];

    if (modelDist < 0.0001 || !modelDist) {
        l.warn(
            "createSheetProjection: modelDist is too small or not exist, the data from server is not valid",
            modelDist,
        );
    }
    if (sheetWidth < 0.0001 || !sheetWidth) {
        l.warn(
            "createSheetProjection: sheetWidth is too small or not exist, the data from server is not valid",
            sheetWidth,
        );
    }
    if (sheetHeight < 0.0001 || !sheetHeight) {
        l.warn(
            "createSheetProjection: sheetHeight is too small or not exist, the data from server is not valid",
            sheetHeight,
        );
    }

    const rotationOffset = Math.atan2(drawingVecY, drawingVecX) - Math.atan2(modelVecY, modelVecX);

    return {
        modelOriginX: modelA[0],
        modelOriginY: modelA[1],
        drawingOriginX: drawingA[0],
        drawingOriginY: drawingA[1],
        cos: Math.cos(rotationOffset),
        sin: Math.sin(rotationOffset),
        scale: drawingDist / modelDist,
        sheetWidth,
        sheetHeight,
        rotationOffset,
    };
}

/**
 * Place a model-space position on the sheet, as a fraction of the sheet on each
 * axis. `0..1` is on the page; y is flipped because sheet coordinates count up
 * from the bottom while the fraction is measured from the top.
 */
export function projectToSheet(projection: SheetProjection, modelX: number, modelY: number): { x: number; y: number } {
    const { modelOriginX, modelOriginY, drawingOriginX, drawingOriginY, cos, sin, scale, sheetWidth, sheetHeight } =
        projection;

    const relativeX = modelX - modelOriginX;
    const relativeY = modelY - modelOriginY;

    const drawingX = (relativeX * cos - relativeY * sin) * scale + drawingOriginX;
    const drawingY = (relativeX * sin + relativeY * cos) * scale + drawingOriginY;

    return { x: drawingX / sheetWidth, y: (sheetHeight - drawingY) / sheetHeight };
}

/**
 * Signed distance from the camera to the nearest edge of a sheet's calibrated
 * area, as a fraction of the sheet.
 *
 * Positive means the camera is on the sheet and the value says how far from the
 * nearest edge it sits; negative means off it. Comparing this across sheets
 * resolves overlapping extents to the one the camera is most central to, which
 * matters because a sheet's page bounds usually reach past the area it actually
 * documents (title blocks, margins).
 */
export function sheetEdgeDistance(projection: SheetProjection, camera: RenderStateCamera): number {
    const { x, y } = projectToSheet(projection, camera.position[0], camera.position[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return Number.NEGATIVE_INFINITY;
    }
    return Math.min(x, 1 - x, y, 1 - y);
}

export function isCameraOnSheet(projection: SheetProjection, camera: RenderStateCamera): boolean {
    return sheetEdgeDistance(projection, camera) >= 0;
}
